import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Compass, Calendar, CalendarClock } from 'lucide-react';
import { PublicNav } from '@/components/public/PublicNav';
import { Footer } from '@/components/public/Footer';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import api from '@/lib/api';
import { formatDate, formatBRL } from '@/lib/format';

const UPCOMING = ['planejada', 'confirmada'];

export default function Gallery() {
  const [searchParams] = useSearchParams();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => (searchParams.get('tab') === 'realizadas' ? 'realizadas' : 'proximas'));
  const [filter, setFilter] = useState('todos');
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get('/public/trips')
      .then((r) => {
        const data = r.data || [];
        setTrips(data);
        // Por padrão prioriza as próximas viagens; cai para "já realizadas" se não houver próximas
        if (!searchParams.get('tab')) {
          const hasUpcoming = data.some((t) => UPCOMING.includes(t.status));
          setTab(hasUpcoming ? 'proximas' : 'realizadas');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [searchParams]);

  const realizadas = useMemo(() => trips.filter((t) => t.status === 'executada'), [trips]);
  const proximas = useMemo(() => trips.filter((t) => UPCOMING.includes(t.status)), [trips]);
  const isUpcoming = tab === 'proximas';
  const current = isUpcoming ? proximas : realizadas;

  const destinos = useMemo(() => {
    const set = new Set(current.map((t) => t.destino).filter(Boolean));
    return ['todos', ...Array.from(set)];
  }, [current]);

  const visible = filter === 'todos' ? current : current.filter((t) => t.destino === filter);

  const changeTab = (v) => { setTab(v); setFilter('todos'); };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />
      <section className="section-wash border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Portfólio de Viagens</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">Conheça as experiências que já realizamos e as próximas viagens programadas. Inspire-se para o seu próximo destino.</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={tab} onValueChange={changeTab} className="mb-8">
            <TabsList data-testid="gallery-tabs">
              <TabsTrigger value="realizadas" data-testid="tab-realizadas">Já realizadas ({realizadas.length})</TabsTrigger>
              <TabsTrigger value="proximas" data-testid="tab-proximas">Próximas viagens ({proximas.length})</TabsTrigger>
            </TabsList>
          </Tabs>

          {destinos.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="public-gallery-filters">
              {destinos.map((d) => (
                <button key={d} onClick={() => setFilter(d)}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${filter === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-accent/50'}`}>
                  {d === 'todos' ? 'Todos' : d}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : visible.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-16 text-center" data-testid="gallery-empty">
              {isUpcoming ? <CalendarClock className="h-10 w-10 mx-auto text-muted-foreground" /> : <Compass className="h-10 w-10 mx-auto text-muted-foreground" />}
              <p className="mt-4 text-muted-foreground">
                {isUpcoming
                  ? 'Nenhuma viagem programada no momento. Volte em breve!'
                  : 'Ainda não há viagens realizadas publicadas. Volte em breve!'}
              </p>
            </div>
          ) : (
            <div className="masonry columns-1 sm:columns-2 lg:columns-3">
              {visible.map((t) => (
                <button key={t.trip_id} onClick={() => setActive(t)}
                  className="group block w-full text-left rounded-2xl overflow-hidden card-shadow bg-card" data-testid="public-gallery-card">
                  <div className="relative overflow-hidden">
                    {t.fotos && t.fotos[0] ? (
                      <img src={t.fotos[0]} alt={t.nome} className="w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" />
                    ) : (
                      <div className="h-56 w-full section-wash flex items-center justify-center"><Compass className="h-8 w-8 text-primary/40" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {isUpcoming && (
                      <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground hover:bg-primary gap-1">
                        <CalendarClock className="h-3 w-3" /> Próxima viagem
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {t.destino}</div>
                      {t.supplier_nome && <Badge variant="secondary" className="text-[10px]">{t.supplier_nome}</Badge>}
                    </div>
                    <h3 className="mt-1.5 font-semibold text-lg">{t.nome}</h3>
                    {isUpcoming && t.data_inicio && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="gallery-card-date">
                        <Calendar className="h-3.5 w-3.5" /> Prevista para {formatDate(t.data_inicio)}
                      </div>
                    )}
                    {t.descricao_publica && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{t.descricao_publica}</p>}
                    {t.preco_venda != null && t.preco_venda > 0 && (
                      <div className="mt-2 text-primary font-semibold" data-testid="gallery-card-price">A partir de {formatBRL(t.preco_venda)}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="public-gallery-lightbox">
          {active && (
            <div>
              {active.fotos && active.fotos.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {active.fotos.map((f, i) => (
                      <CarouselItem key={i}>
                        <img src={f} alt={`${active.nome} ${i + 1}`} className="w-full max-h-[60vh] object-cover" />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {active.fotos.length > 1 && (<><CarouselPrevious className="left-3" /><CarouselNext className="right-3" /></>)}
                </Carousel>
              ) : (
                <div className="h-64 section-wash flex items-center justify-center"><Compass className="h-10 w-10 text-primary/40" /></div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {active.destino}</span>
                  {active.data_inicio && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      {UPCOMING.includes(active.status) ? 'Prevista para ' : ''}{formatDate(active.data_inicio)}
                    </span>
                  )}
                  {UPCOMING.includes(active.status) && (
                    <Badge className="bg-primary text-primary-foreground hover:bg-primary gap-1"><CalendarClock className="h-3 w-3" /> Próxima viagem</Badge>
                  )}
                </div>
                <h3 className="mt-2 font-display text-2xl font-semibold">{active.nome}</h3>
                {active.preco_venda != null && active.preco_venda > 0 && (
                  <div className="mt-1 text-primary font-semibold text-lg">A partir de {formatBRL(active.preco_venda)}</div>
                )}
                {active.descricao_publica && <p className="mt-2 text-muted-foreground leading-relaxed">{active.descricao_publica}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
